import {
  getFoodRankMap,
  type FoodRankMap,
} from '@/utils/nutrition/getFoodRankMap';

import type {
  GetFoodRankResponse,
  GetAllFoodsResponse,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { Skeleton } from '@design-system/Skeleton';
import { MyFoodsCard } from '@design-system/Cards/MyFoodsCard';
import { DISPLAY, type DisplayMode } from './MyFoodsPage.model';

export interface MyFoodsPageFoodsGridProps {
  isLoading?: boolean;
  displayMode?: DisplayMode;
  foods?: GetAllFoodsResponse;
  foodRank?: GetFoodRankResponse;
}

const renderMyFoods = (
  foods: GetAllFoodsResponse,
  rankMap?: FoodRankMap,
  displayMode?: DisplayMode,
) => {
  const variant = displayMode === DISPLAY.GRID ? 'default' : 'flat';

  const renderedFoods = foods.map((food) => (
    <MyFoodsCard
      food={food}
      key={`${food.title}-${food.id}-page`}
      variant={variant}
      rankMap={rankMap}
    />
  ));

  return renderedFoods;
};

export const MyFoodsPageFoodsGrid = ({
  foods,
  foodRank,
  isLoading,
  displayMode,
}: MyFoodsPageFoodsGridProps) => {
  const rankMap = getFoodRankMap(foodRank);

  if (isLoading) {
    return (
      <div
        data-display-mode={displayMode}
        className='grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 data-[display-mode="list"]:lg:grid-cols-2'
      >
        {Array.from({ length: 24 }).map((_, index) => (
          <Skeleton
            key={`food-page-skeleton-${index}`}
            data-display-mode={displayMode}
            className="w-full h-64 data-[display-mode='list']:h-24"
          />
        ))}
      </div>
    );
  }

  if (!foods?.length) {
    return (
      <div className="flex flex-col gap-4 py-14 text-center">
        <span className="typography-h3 text-primary">No results found</span>

        <span className="typography-body text-secondary">
          Try adjusting your filters to see more results
        </span>
      </div>
    );
  }

  return (
    <div
      data-display-mode={displayMode}
      className='grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 data-[display-mode="list"]:lg:grid-cols-2'
    >
      {renderMyFoods(foods, rankMap, displayMode)}
    </div>
  );
};
